import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookingContext } from "@modules/booking/context/BookingContext";
import { DayRowData } from "@modules/booking/context/types";
import { getClassroomClashesQuery } from "@modules/booking/graphql/getClassroomClashes.query";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";
import { getEditedClassroomAssignedDates } from "@modules/booking/utils/classroomEvents";
import { useApolloClient } from "@apollo/client";
import { ClassroomBookingClash, CubeQuery } from "@models/ISchema";
import {
  Button,
  CardNotification,
  Drawer,
  Heading,
  Link,
  SkeletonBase,
  Table,
  toast,
} from "@foris/avocado-suite";
import { getColumnCell } from "@common/utils/table.util";
import { getDateNumber } from "@utils/formats";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import useBookingAccess from "@common/hooks/useBookingAccess";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import styles from "./classroomClashesDrawer.module.scss";

dayjs.extend(utc);

interface ClassroomClashesDrawerProps {
  isOpen: boolean;
  missingDates?: DayRowData[];
  onClose: () => void;
}

const PAGINATION = {
  page: 1,
  size: 10,
};

const ClassroomClashesDrawer = ({
  isOpen,
  missingDates = [],
  onClose,
}: ClassroomClashesDrawerProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const client = useApolloClient();
  const {} = useNavigationUrl();
  const { origin, scenario, contextUrl } = useBookingContextUrl();
  const { isAdminBooking } = useBookingAccess();
  const { state } = useContext(BookingContext);
  const [totalClashes, setTotalClashes] = useState(0);
  const [classroomBookingClashes, setClassroomBookingClashes] = useState<ClassroomBookingClash[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);

  const getClassroomClashes = async () => {
    setIsLoading(true);

    const selectedClassrooms = state?.search?.selectedClassrooms?.map(selection => ({
      classroom: selection?.classroom,
      dates: missingDates,
    }));
    const assignedClassrooms = getEditedClassroomAssignedDates(selectedClassrooms);
    const classroomIds = assignedClassrooms?.map(classroom => classroom?.classroomId);
    const dateAvailability = assignedClassrooms?.flatMap(classroom => classroom?.assignedDates);

    const { data } = await client.query({
      query: getClassroomClashesQuery,
      variables: {
        scenarioId: scenario,
        originId: origin,
        filter: {
          pagination: PAGINATION,
          fields: {
            classroomId: {
              in: classroomIds,
            },
            dateAvailability,
          },
        },
      },
    });

    const cube = data?.cube as CubeQuery;
    const newTotalClashes = cube?.classroomBookingClashes?.pageInfo?.total ?? 0;

    setClassroomBookingClashes(cube?.classroomBookingClashes?.items ?? []);

    if (totalClashes && newTotalClashes < totalClashes) {
      toast.success(
        t("booking.booking-request.notifications.resolved-clashes.title", {
          count: totalClashes - newTotalClashes,
        }),
      );
    }

    setTotalClashes(newTotalClashes);
    setIsLoading(false);
  };

  const clashesMessages = useMemo(
    () =>
      classroomBookingClashes?.map(clash => {
        // dateAvailability is in the format "YYYY-MM-DD HH:MM:SS - HH:MM:SS"
        const [date, startTime, , endTime] = clash?.dateAvailability?.split(" ");

        // convert time to required format HH:MM
        const [startHour, startMinute] = startTime?.split(":");
        const [endHour, endMinute] = endTime?.split(":");

        const formattedDate = `${dayjs(date).format("DD.MM.YYYY")}  ${getDateNumber(
          startHour || 0,
        )}:${getDateNumber(startMinute || 0)} - ${getDateNumber(endHour || 0)}:${getDateNumber(
          endMinute || 0,
        )}`;

        if (!!clash?.classroomBooking) {
          return {
            date: formattedDate,
            title: t("booking.booking-request.drawer-clashes.messages.booking-clash", {
              bookingTitle: clash?.classroomBooking?.title,
            }),
            booking: clash?.classroomBooking,
            session: null,
            classroom: clash?.classroom,
          };
        }

        const courseName = clash?.session?.links?.[0]?.course?.name ?? null;

        return {
          date: formattedDate,
          title: `${t("booking.booking-request.drawer-clashes.messages.session-clash", {
            sessionId: clash?.session?.id,
          })} ${
            courseName
              ? t("booking.booking-request.drawer-clashes.messages.session-clash-course", {
                  courseName: courseName,
                })
              : ""
          })}`,
          session: clash?.session,
          booking: null,
          classroom: clash?.classroom,
        };
      }),
    [classroomBookingClashes, language],
  );

  useEffect(() => {
    if (isOpen) {
      getClassroomClashes();
    } else {
      setClassroomBookingClashes([]);
      setTotalClashes(0);
    }
  }, [isOpen]);

  return (
    <Drawer
      isOpen={isOpen}
      title={<Heading type="h3">{t("booking.booking-request.drawer-clashes.title")}</Heading>}
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t("booking.booking-request.actions.close")}
        </Button>
      }
      onClose={onClose}
      closeOnOverlayClick
    >
      <div className={styles.classroomClashesDrawer}>
        {isLoading ? (
          <div className={styles.classroomClashesDrawer_skeleton}>
            <SkeletonBase width="100%" height="18px" />

            <div className={styles.classroomClashesDrawer_skeletonCards}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div className={styles.classroomClashesDrawer_skeletonCard} key={index}>
                  <SkeletonBase width="24px" height="24px" />

                  <div className={styles.classroomClashesDrawer_skeletonCardContent}>
                    <SkeletonBase width="100%" height="16px" />
                    <div className={styles.classroomClashesDrawer_skeletonCardActions}>
                      <SkeletonBase width="97px" height="8px" />
                      <SkeletonBase width="97px" height="8px" />
                    </div>
                  </div>
                </div>
              ))}

              <SkeletonBase
                className={styles.classroomClashesDrawer_skeletonFooter}
                width="200px"
                height="18px"
              />
            </div>
          </div>
        ) : (
          <>
            <CardNotification
              title={
                !!totalClashes
                  ? t("booking.booking-request.notifications.drawer-clash-info.title")
                  : t("booking.booking-request.notifications.drawer-clash-info-available.title")
              }
              state={!!totalClashes ? "info" : "success"}
            >
              {!!totalClashes
                ? t("booking.booking-request.notifications.drawer-clash-info.description")
                : t(
                    "booking.booking-request.notifications.drawer-clash-info-available.description",
                  )}
            </CardNotification>

            {!!totalClashes && (
              <>
                <div className={styles.classroomClashesDrawer_header}>
                  <Heading type="h4" className={styles.classroomClashesDrawer_count}>
                    {t("booking.booking-request.drawer-clashes.count", { count: totalClashes })}
                  </Heading>
                  <Button leftIcon="refresh" variant="ghost" onClick={getClassroomClashes}>
                    {t("booking.booking-request.actions.update")}
                  </Button>
                </div>

                <Table
                  data={clashesMessages}
                  columns={[
                    {
                      header: t("booking.booking-request.drawer-clashes.date"),
                      key: "date",
                      renderCell: (row: any) => getColumnCell(row?.date),
                      desktopOptions: {
                        headCellStyles: {
                          width: "190px",
                        },
                      },
                    },
                    {
                      header: t("booking.booking-request.drawer-clashes.classroom"),
                      key: "classroom",
                      renderCell: (row: any) => getColumnCell(row?.classroom?.name || "-"),
                      desktopOptions: {
                        headCellStyles: {
                          width: "150px",
                        },
                      },
                    },
                    {
                      header: t("booking.booking-request.drawer-clashes.conflict"),
                      key: "conflict",
                      renderCell: (row: any) => (
                        <div className={styles.classroomClashesDrawer_conflict}>
                          {getColumnCell(row?.title)}
                          {isAdminBooking && (
                            <Link
                              size="sm"
                              onClick={() => {
                                const baseUrl = window?.location?.origin;

                                if (row?.booking) {
                                  window.open(
                                    `${baseUrl}/booking/detail/${contextUrl}/${row?.booking?.id}`,
                                    "_blank",
                                  );
                                  return;
                                }

                                if (row?.session && row?.session?.links?.length) {
                                  window.open(
                                    `${baseUrl}/editor/form-edit/${contextUrl}/${row?.session?.links?.[0]?.id}`,
                                    "_blank",
                                  );
                                }
                              }}
                              colored
                            >
                              {row?.booking
                                ? t(
                                    "booking.booking-request.drawer-clashes.messages.go-to-booking-edit",
                                  )
                                : t(
                                    "booking.booking-request.drawer-clashes.messages.go-to-session-edit",
                                  )}
                            </Link>
                          )}
                        </div>
                      ),
                    },
                  ]}
                />
              </>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default ClassroomClashesDrawer;
